import { makeAutoObservable, runInAction, reaction } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";

/**
 * Payment Source Types per spec 08-accounts-receivable.md (AR-01, AR-02)
 */
export type SourceType = "customer" | "vendor" | "employee" | "intercompany" | "other";

/**
 * Payment Types
 */
export type PaymentType = "invoice_payment" | "refund" | "ica_settlement" | "misc_income";

/**
 * Payment Methods
 */
export type PaymentMethod = "check" | "ach" | "wire" | "credit_card" | "cash";

/**
 * ICA Status
 */
export type ICAStatus = "pending" | "acknowledged" | "settled";

/**
 * Deposit Types
 */
export type DepositType = "cash_check" | "credit_card" | "ach";

/**
 * Deposit record for bank reconciliation
 */
export interface Deposit {
  id: string;
  depositDate: string;
  depositType: DepositType;
  bankAccountId: string;
  bankAccountName: string;
  referenceNumber: string;
  totalAmount: number;
  paymentIds: string[];
  notes?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
}

/**
 * Deposit Form Data
 */
export interface DepositFormData {
  depositDate: string;
  depositType: DepositType;
  bankAccountId: string;
  referenceNumber: string;
  selectedPaymentIds: string[];
  notes: string;
}

/**
 * Entity for payment (customer, vendor, employee, etc.)
 */
export interface PaymentEntity {
  id: string;
  type: SourceType;
  name: string;
  phone?: string;
  address?: string;
  email?: string;
}

/**
 * Payment Application (links payment to invoice)
 */
export interface PaymentApplication {
  id: string;
  paymentId: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  appliedAt: Date;
}

/**
 * AR Payment record
 */
export interface ARPayment {
  id: string;
  paymentType: PaymentType;
  sourceType: SourceType;
  sourceId: string;
  sourceName: string;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  amount: number;
  referenceNumber: string; // check #, CC auth, etc.
  appliedTo: PaymentApplication[];
  unappliedAmount: number;
  depositBatchId?: string;
  bankAccountId?: string;
  bankAccountName?: string;
  notes?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
}

/**
 * ICA Record Side Type (Phase 3 - Two-Way Sync)
 */
export type ICASide = "ap" | "ar";

/**
 * ICA Transaction (AR-03) - Enhanced for Two-Way Sync (Phase 3.2)
 */
export interface ICATransaction {
  id: string;
  fromCompanyId: string;
  fromCompanyName: string;
  toCompanyId: string;
  toCompanyName: string;
  invoiceNumber: string;
  transactionDate: string;
  description: string;
  amount: number;
  clearingAccountId: string; // 888 account
  status: ICAStatus;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;
  settledAt?: Date;
  settledBy?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
  // Phase 3.2: Two-Way Sync fields
  linkedTransactionId?: string; // Links AP and AR records together
  side: ICASide; // Whether this is the AP side (initiator) or AR side (receiver)
  transactionType?: ICATransactionType;
  lineItems?: ICALineItem[]; // GL breakdown for this side
  canEditGL?: boolean; // AR side can edit their GL accounts
  syncedAt?: Date; // When the linked record was last synced
}

/**
 * Customer Invoice for AR
 */
export interface ARInvoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  invoiceDate: string;
  dueDate: string;
  amount: number;
  amountPaid: number;
  balance: number;
  status: string;
  daysOutstanding: number;
  agingBucket: "current" | "30" | "60" | "90" | "120+";
}

/**
 * Aging Summary
 */
export interface AgingSummary {
  current: number;
  days30: number;
  days60: number;
  days90: number;
  days120Plus: number;
  total: number;
}

/**
 * Quick Payment Form Data (AR-05)
 */
export interface QuickPaymentFormData {
  sourceType: SourceType;
  sourceId: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  amount: number;
  referenceNumber: string;
  paymentDate: string;
  bankAccountId: string;
  selectedInvoiceIds: string[];
  notes: string;
}

/**
 * ICA Line Item for multi-line GL breakdown
 */
export interface ICALineItem {
  id: string;
  amount: number;
  glAccountId: string;
  glAccountName: string;
  description: string;
}

/**
 * ICA Target Company with its own line items
 */
export interface ICATargetCompany {
  companyId: string;
  lineItems: ICALineItem[];
}

/**
 * ICA Transaction Type
 */
export type ICATransactionType = "check" | "transfer";

/**
 * ICA Form Data (AR-03) - Enhanced for multi-company and multi-line support
 */
export interface ICAFormData {
  fromCompanyId: string;
  toCompanies: ICATargetCompany[];
  invoiceNumber: string;
  transactionDate: string;
  transactionType: ICATransactionType;
  notes: string;
  // Legacy single fields for backward compatibility
  toCompanyId: string;
  description: string;
  amount: number;
}

/**
 * Search Criteria (AR-04)
 */
export interface ARSearchCriteria {
  invoiceNumber: string;
  customerName: string;
  phone: string;
  address: string;
  dateFrom: string;
  dateTo: string;
}

/**
 * Aging Bucket Type
 */
export type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+";

/**
 * Advanced Search State (shared between popover and aging chart)
 */
export interface AdvancedSearchState {
  isOpen: boolean;
  quickSearch: string; // Searches across invoice, customer, phone simultaneously
  invoice: string;
  customer: string;
  phone: string;
  sourceType: SourceType | "all";
  exactAmount: string;
  minAmount: string;
  maxAmount: string;
  dateFrom: string;
  dateTo: string;
}

/**
 * Miller Precast Companies for ICA
 */
export const MILLER_COMPANIES = [
  { id: "company-h2", name: "H2 Industries", code: "H2" },
  { id: "company-cpp", name: "Columbia Precast Products", code: "CPP" },
  { id: "company-rvp", name: "Rogue Valley Precast", code: "RVP" },
  { id: "company-snd", name: "Sound Precast", code: "Sound" },
  { id: "company-brg", name: "Bridger Precast", code: "Bridger" },
];

/**
 * Common GL Accounts for ICA transactions
 */
export const ICA_GL_ACCOUNTS = [
  { id: "gl-888", code: "888", name: "ICA Clearing" },
  { id: "gl-6100", code: "6100", name: "Labor" },
  { id: "gl-6200", code: "6200", name: "Repairs & Maintenance" },
  { id: "gl-5400", code: "5400", name: "Travel & Mileage" },
  { id: "gl-5100", code: "5100", name: "Materials" },
  { id: "gl-5200", code: "5200", name: "Equipment Rental" },
  { id: "gl-4100", code: "4100", name: "Revenue - Services" },
  { id: "gl-4200", code: "4200", name: "Revenue - Products" },
];

/**
 * Accounts Receivable Store
 *
 * Manages AR data per spec 08-accounts-receivable.md
 * - Payment from any entity (AR-01)
 * - Vendor refund receipt (AR-02)
 * - Intercompany invoicing (AR-03)
 * - Search by invoice, phone, address (AR-04)
 * - Streamlined payment entry (AR-05)
 * - Customer statements (AR-06)
 * - AR aging reports (AR-07)
 */
class AccountsReceivableStore {
  rootStore: RootStore;

  // Quick Payment Form (AR-05)
  quickPaymentForm: QuickPaymentFormData = this.getEmptyQuickPaymentForm();
  currentStep: number = 1; // Track steps for < 5 click goal

  // ICA Form (AR-03)
  icaForm: ICAFormData = this.getEmptyICAForm();

  // Deposit Form
  depositForm: DepositFormData = this.getEmptyDepositForm();

  // Search (AR-04)
  searchCriteria: ARSearchCriteria = this.getEmptySearchCriteria();
  searchResults: ARInvoice[] = [];
  isSearching: boolean = false;

  // Advanced Search (popover)
  advancedSearch: AdvancedSearchState = this.getEmptyAdvancedSearch();

  // Filters
  companyFilter: string | null = null;
  sourceTypeFilter: SourceType | "all" = "all";
  paymentTypeFilter: PaymentType | "all" = "all";
  dateRangeStart: string = "";
  dateRangeEnd: string = "";
  showOverdueOnly: boolean = false;

  // UI State
  error: string | null = null;
  selectedPayment: ARPayment | null = null;
  selectedICA: ICATransaction | null = null;

  // Bank Accounts
  bankAccounts = [
    { id: "bank-001", name: "Operating Account", accountNumber: "****1234" },
    { id: "bank-002", name: "Payroll Account", accountNumber: "****5678" },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Sync company filter with global AppLayout selection
    reaction(
      () => rootStore.appLayoutStore?.selectedCompanyId,
      (companyId) => {
        this.companyFilter = companyId;
      },
      { fireImmediately: true }
    );
  }

  // ==================== ServiceStore Getters ====================

  /**
   * Access the ar-payments ServiceStore
   */
  get arPaymentsServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["ar-payments"] as ServiceStore;
  }

  /**
   * Access the ar-invoices ServiceStore
   */
  get arInvoicesServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["ar-invoices"] as ServiceStore;
  }

  /**
   * Access the ica-transactions ServiceStore
   */
  get icaTransactionsServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["ica-transactions"] as ServiceStore;
  }

  /**
   * Access the payment-entities ServiceStore
   */
  get paymentEntitiesServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["payment-entities"] as ServiceStore;
  }

  /**
   * Access the ar-deposits ServiceStore
   */
  get arDepositsServiceStore(): ServiceStore {
    return this.rootStore.serviceStores?.["ar-deposits"] as ServiceStore;
  }

  // ==================== Data Computed Getters (replace local arrays) ====================

  /**
   * Payments from ServiceStore (replaces local payments array)
   */
  get payments(): ARPayment[] {
    return (this.arPaymentsServiceStore?.entities as unknown as ARPayment[]) || [];
  }

  /**
   * Invoices from ServiceStore (replaces local invoices array)
   */
  get invoices(): ARInvoice[] {
    return (this.arInvoicesServiceStore?.entities as unknown as ARInvoice[]) || [];
  }

  /**
   * ICA Transactions from ServiceStore (replaces local icaTransactions array)
   */
  get icaTransactions(): ICATransaction[] {
    return (this.icaTransactionsServiceStore?.entities as unknown as ICATransaction[]) || [];
  }

  /**
   * Payment entities from ServiceStore (replaces local entities array)
   */
  get entities(): PaymentEntity[] {
    return (this.paymentEntitiesServiceStore?.entities as unknown as PaymentEntity[]) || [];
  }

  /**
   * Deposits from ServiceStore (replaces local deposits array)
   */
  get deposits(): Deposit[] {
    return (this.arDepositsServiceStore?.entities as unknown as Deposit[]) || [];
  }

  /**
   * Loading state from ServiceStore (replaces local loading observable)
   */
  get loading(): boolean {
    return (
      this.arPaymentsServiceStore?.isFindPending ||
      this.arInvoicesServiceStore?.isFindPending ||
      this.icaTransactionsServiceStore?.isFindPending ||
      this.paymentEntitiesServiceStore?.isFindPending ||
      this.arDepositsServiceStore?.isFindPending ||
      false
    );
  }

  // ==================== Computed Properties ====================

  /**
   * AR Aging Summary (AR-07)
   */
  get agingSummary(): AgingSummary {
    const summary: AgingSummary = {
      current: 0,
      days30: 0,
      days60: 0,
      days90: 0,
      days120Plus: 0,
      total: 0,
    };

    this.invoices.forEach((inv) => {
      if (inv.balance <= 0) return;

      summary.total += inv.balance;

      switch (inv.agingBucket) {
        case "current":
          summary.current += inv.balance;
          break;
        case "30":
          summary.days30 += inv.balance;
          break;
        case "60":
          summary.days60 += inv.balance;
          break;
        case "90":
          summary.days90 += inv.balance;
          break;
        case "120+":
          summary.days120Plus += inv.balance;
          break;
      }
    });

    return summary;
  }

  /**
   * Filtered payments - respects advanced search criteria
   */
  get filteredPayments(): ARPayment[] {
    let filtered = [...this.payments];
    const search = this.advancedSearch;

    // Legacy filters
    if (this.sourceTypeFilter !== "all") {
      filtered = filtered.filter((p) => p.sourceType === this.sourceTypeFilter);
    }

    if (this.paymentTypeFilter !== "all") {
      filtered = filtered.filter((p) => p.paymentType === this.paymentTypeFilter);
    }

    if (this.dateRangeStart) {
      filtered = filtered.filter((p) => p.paymentDate >= this.dateRangeStart);
    }

    if (this.dateRangeEnd) {
      filtered = filtered.filter((p) => p.paymentDate <= this.dateRangeEnd);
    }

    // Quick search - matches source name or reference number
    if (search.quickSearch.length >= 2) {
      const term = search.quickSearch.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.sourceName.toLowerCase().includes(term) ||
          p.referenceNumber?.toLowerCase().includes(term)
      );
    }

    // Advanced search filters
    if (search.customer.length >= 2) {
      const term = search.customer.toLowerCase();
      filtered = filtered.filter((p) => p.sourceName.toLowerCase().includes(term));
    }

    if (search.sourceType !== "all") {
      filtered = filtered.filter((p) => p.sourceType === search.sourceType);
    }

    if (search.dateFrom) {
      filtered = filtered.filter((p) => p.paymentDate >= search.dateFrom);
    }

    if (search.dateTo) {
      filtered = filtered.filter((p) => p.paymentDate <= search.dateTo);
    }

    // Amount filters
    if (search.exactAmount) {
      const exact = parseFloat(search.exactAmount);
      filtered = filtered.filter((p) => Math.abs(p.amount - exact) < 0.01);
    } else {
      if (search.minAmount) {
        const min = parseFloat(search.minAmount);
        filtered = filtered.filter((p) => p.amount >= min);
      }
      if (search.maxAmount) {
        const max = parseFloat(search.maxAmount);
        filtered = filtered.filter((p) => p.amount <= max);
      }
    }

    return filtered.sort((a, b) => b.paymentDate.localeCompare(a.paymentDate));
  }

  /**
   * Open invoices (unpaid balance) - respects advanced search criteria
   */
  get openInvoices(): ARInvoice[] {
    let filtered = this.invoices.filter((inv) => inv.balance > 0);
    const search = this.advancedSearch;

    if (this.showOverdueOnly) {
      filtered = filtered.filter((inv) => inv.daysOutstanding > 30);
    }

    // Quick search - matches invoice number, customer name, or phone
    if (search.quickSearch.length >= 2) {
      const term = search.quickSearch.toLowerCase();
      const termDigits = search.quickSearch.replace(/\D/g, "");
      filtered = filtered.filter((inv) => {
        // Match invoice number
        if (inv.invoiceNumber.toLowerCase().includes(term)) return true;
        // Match customer name
        if (inv.customerName.toLowerCase().includes(term)) return true;
        // Match phone (if digits provided)
        if (termDigits.length >= 3) {
          const entity = this.entities.find((e) => e.id === inv.customerId);
          const entityPhone = entity?.phone?.replace(/\D/g, "") || "";
          if (entityPhone.includes(termDigits)) return true;
        }
        return false;
      });
    }

    // Advanced search filters
    if (search.invoice.length >= 2) {
      const term = search.invoice.toLowerCase();
      filtered = filtered.filter((inv) =>
        inv.invoiceNumber.toLowerCase().includes(term)
      );
    }

    if (search.customer.length >= 2) {
      const term = search.customer.toLowerCase();
      filtered = filtered.filter((inv) =>
        inv.customerName.toLowerCase().includes(term)
      );
    }

    if (search.phone.length >= 2) {
      const phoneDigits = search.phone.replace(/\D/g, "");
      filtered = filtered.filter((inv) => {
        const entity = this.entities.find((e) => e.id === inv.customerId);
        const entityPhone = entity?.phone?.replace(/\D/g, "") || "";
        return entityPhone.includes(phoneDigits);
      });
    }

    if (search.dateFrom) {
      filtered = filtered.filter((inv) => inv.invoiceDate >= search.dateFrom);
    }

    if (search.dateTo) {
      filtered = filtered.filter((inv) => inv.invoiceDate <= search.dateTo);
    }

    // Amount filters (on balance)
    if (search.exactAmount) {
      const exact = parseFloat(search.exactAmount);
      filtered = filtered.filter((inv) => Math.abs(inv.balance - exact) < 0.01);
    } else {
      if (search.minAmount) {
        const min = parseFloat(search.minAmount);
        filtered = filtered.filter((inv) => inv.balance >= min);
      }
      if (search.maxAmount) {
        const max = parseFloat(search.maxAmount);
        filtered = filtered.filter((inv) => inv.balance <= max);
      }
    }

    return filtered;
  }

  /**
   * Pending ICA transactions
   */
  get pendingICA(): ICATransaction[] {
    return this.icaTransactions.filter((t) => t.status === "pending");
  }

  /**
   * Metrics for dashboard
   */
  get metrics() {
    const totalReceivable = this.invoices.reduce((sum, inv) => sum + inv.balance, 0);
    const overdueAmount = this.invoices
      .filter((inv) => inv.daysOutstanding > 30)
      .reduce((sum, inv) => sum + inv.balance, 0);
    const unappliedPayments = this.payments.reduce(
      (sum, p) => sum + p.unappliedAmount,
      0
    );

    return {
      totalReceivable,
      invoiceCount: this.openInvoices.length,
      overdueAmount,
      overdueCount: this.invoices.filter((inv) => inv.daysOutstanding > 30).length,
      unappliedPayments,
      pendingICACount: this.pendingICA.length,
      todayPayments: this.payments.filter(
        (p) => p.paymentDate === new Date().toISOString().split("T")[0]
      ).length,
    };
  }

  /**
   * Invoices for selected entity in quick payment
   */
  get entityInvoices(): ARInvoice[] {
    if (!this.quickPaymentForm.sourceId) return [];
    return this.invoices.filter(
      (inv) =>
        inv.customerId === this.quickPaymentForm.sourceId && inv.balance > 0
    );
  }

  /**
   * Undeposited payments (not yet assigned to a deposit)
   */
  get undepositedPayments(): ARPayment[] {
    const depositedPaymentIds = new Set(
      this.deposits.flatMap((d) => d.paymentIds)
    );
    return this.payments.filter((p) => !depositedPaymentIds.has(p.id));
  }

  /**
   * Undeposited payments filtered by deposit type (for deposit form)
   */
  get undepositedPaymentsByType(): ARPayment[] {
    const depositType = this.depositForm.depositType;
    return this.undepositedPayments.filter((p) => {
      if (depositType === "cash_check") {
        return p.paymentMethod === "cash" || p.paymentMethod === "check";
      } else if (depositType === "credit_card") {
        return p.paymentMethod === "credit_card";
      } else if (depositType === "ach") {
        return p.paymentMethod === "ach" || p.paymentMethod === "wire";
      }
      return true;
    });
  }

  /**
   * Selected payments for deposit form
   */
  get selectedDepositPayments(): ARPayment[] {
    return this.payments.filter((p) =>
      this.depositForm.selectedPaymentIds.includes(p.id)
    );
  }

  /**
   * Total amount for deposit form
   */
  get depositFormTotal(): number {
    return this.selectedDepositPayments.reduce((sum, p) => sum + p.amount, 0);
  }

  /**
   * Filtered deposits - respects advanced search criteria
   */
  get filteredDeposits(): Deposit[] {
    let filtered = [...this.deposits];
    const search = this.advancedSearch;

    // Quick search - matches reference number or bank account name
    if (search.quickSearch.length >= 2) {
      const term = search.quickSearch.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.referenceNumber?.toLowerCase().includes(term) ||
          d.bankAccountName.toLowerCase().includes(term)
      );
    }

    // Date range filter
    if (search.dateFrom) {
      filtered = filtered.filter((d) => d.depositDate >= search.dateFrom);
    }

    if (search.dateTo) {
      filtered = filtered.filter((d) => d.depositDate <= search.dateTo);
    }

    // Amount filters
    if (search.exactAmount) {
      const exact = parseFloat(search.exactAmount);
      filtered = filtered.filter((d) => Math.abs(d.totalAmount - exact) < 0.01);
    } else {
      if (search.minAmount) {
        const min = parseFloat(search.minAmount);
        filtered = filtered.filter((d) => d.totalAmount >= min);
      }
      if (search.maxAmount) {
        const max = parseFloat(search.maxAmount);
        filtered = filtered.filter((d) => d.totalAmount <= max);
      }
    }

    // Reference number search (using invoice field)
    if (search.invoice.length >= 2) {
      const term = search.invoice.toLowerCase();
      filtered = filtered.filter((d) =>
        d.referenceNumber?.toLowerCase().includes(term)
      );
    }

    return filtered.sort((a, b) => b.depositDate.localeCompare(a.depositDate));
  }

  /**
   * Filtered ICA transactions - respects advanced search criteria
   */
  get filteredICATransactions(): ICATransaction[] {
    let filtered = [...this.icaTransactions];
    const search = this.advancedSearch;

    // Quick search - matches invoice number, from/to company names
    if (search.quickSearch.length >= 2) {
      const term = search.quickSearch.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.invoiceNumber.toLowerCase().includes(term) ||
          t.fromCompanyName.toLowerCase().includes(term) ||
          t.toCompanyName.toLowerCase().includes(term) ||
          t.description?.toLowerCase().includes(term)
      );
    }

    // Invoice number filter
    if (search.invoice.length >= 2) {
      const term = search.invoice.toLowerCase();
      filtered = filtered.filter((t) =>
        t.invoiceNumber.toLowerCase().includes(term)
      );
    }

    // Customer/Company name filter (searches both from and to company)
    if (search.customer.length >= 2) {
      const term = search.customer.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.fromCompanyName.toLowerCase().includes(term) ||
          t.toCompanyName.toLowerCase().includes(term)
      );
    }

    // Date range filter
    if (search.dateFrom) {
      filtered = filtered.filter((t) => t.transactionDate >= search.dateFrom);
    }

    if (search.dateTo) {
      filtered = filtered.filter((t) => t.transactionDate <= search.dateTo);
    }

    // Amount filters
    if (search.exactAmount) {
      const exact = parseFloat(search.exactAmount);
      filtered = filtered.filter((t) => Math.abs(t.amount - exact) < 0.01);
    } else {
      if (search.minAmount) {
        const min = parseFloat(search.minAmount);
        filtered = filtered.filter((t) => t.amount >= min);
      }
      if (search.maxAmount) {
        const max = parseFloat(search.maxAmount);
        filtered = filtered.filter((t) => t.amount <= max);
      }
    }

    return filtered.sort((a, b) => b.transactionDate.localeCompare(a.transactionDate));
  }

  // ==================== Filter Actions ====================

  setCompanyFilter = (companyId: string | null) => {
    this.companyFilter = companyId;
  };

  setSourceTypeFilter = (type: SourceType | "all") => {
    this.sourceTypeFilter = type;
  };

  setPaymentTypeFilter = (type: PaymentType | "all") => {
    this.paymentTypeFilter = type;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRangeStart = start;
    this.dateRangeEnd = end;
  };

  setShowOverdueOnly = (value: boolean) => {
    this.showOverdueOnly = value;
  };

  clearFilters = () => {
    this.companyFilter = null;
    this.sourceTypeFilter = "all";
    this.paymentTypeFilter = "all";
    this.dateRangeStart = "";
    this.dateRangeEnd = "";
    this.showOverdueOnly = false;
  };

  // ==================== Quick Payment Form (AR-05) ====================

  getEmptyQuickPaymentForm(): QuickPaymentFormData {
    return {
      sourceType: "customer",
      sourceId: "",
      paymentType: "invoice_payment",
      paymentMethod: "check",
      amount: 0,
      referenceNumber: "",
      paymentDate: new Date().toISOString().split("T")[0],
      bankAccountId: "",
      selectedInvoiceIds: [],
      notes: "",
    };
  }

  resetQuickPaymentForm = () => {
    this.quickPaymentForm = this.getEmptyQuickPaymentForm();
    this.currentStep = 1;
  };

  updateQuickPaymentField = <K extends keyof QuickPaymentFormData>(
    field: K,
    value: QuickPaymentFormData[K]
  ) => {
    this.quickPaymentForm[field] = value;

    // Auto-set payment type based on source type
    if (field === "sourceType") {
      if (value === "vendor") {
        this.quickPaymentForm.paymentType = "refund";
      } else if (value === "intercompany") {
        this.quickPaymentForm.paymentType = "ica_settlement";
      } else {
        this.quickPaymentForm.paymentType = "invoice_payment";
      }
    }
  };

  nextStep = () => {
    if (this.currentStep < 4) {
      this.currentStep++;
    }
  };

  prevStep = () => {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  };

  toggleInvoiceSelection = (invoiceId: string) => {
    const ids = this.quickPaymentForm.selectedInvoiceIds;
    const index = ids.indexOf(invoiceId);
    if (index === -1) {
      ids.push(invoiceId);
    } else {
      ids.splice(index, 1);
    }

    // Auto-calculate total from selected invoices
    const selectedTotal = this.entityInvoices
      .filter((inv) => ids.includes(inv.id))
      .reduce((sum, inv) => sum + inv.balance, 0);
    this.quickPaymentForm.amount = selectedTotal;
  };

  /**
   * Create payment via ServiceStore (< 5 clicks goal)
   */
  createPayment = async () => {
    this.error = null;

    try {
      const entity = this.entities.find(
        (e) => e.id === this.quickPaymentForm.sourceId
      );

      const paymentData = {
        paymentType: this.quickPaymentForm.paymentType,
        sourceType: this.quickPaymentForm.sourceType,
        sourceId: this.quickPaymentForm.sourceId,
        sourceName: entity?.name || "Unknown",
        paymentMethod: this.quickPaymentForm.paymentMethod,
        paymentDate: this.quickPaymentForm.paymentDate,
        amount: this.quickPaymentForm.amount,
        referenceNumber: this.quickPaymentForm.referenceNumber,
        appliedTo: this.quickPaymentForm.selectedInvoiceIds.map((invId) => {
          const inv = this.invoices.find((i) => i.id === invId);
          return {
            invoiceId: invId,
            invoiceNumber: inv?.invoiceNumber || "",
            amount: inv?.balance || 0,
          };
        }),
        unappliedAmount: 0,
        bankAccountId: this.quickPaymentForm.bankAccountId,
        notes: this.quickPaymentForm.notes,
      };

      await this.arPaymentsServiceStore?.create(paymentData);

      // Update invoice balances via ServiceStore patch
      for (const invId of this.quickPaymentForm.selectedInvoiceIds) {
        const inv = this.invoices.find((i) => i.id === invId);
        if (inv) {
          await this.arInvoicesServiceStore?.patch(invId, {
            amountPaid: inv.amountPaid + inv.balance,
            balance: 0,
            status: "paid",
          });
        }
      }

      runInAction(() => {
        this.resetQuickPaymentForm();
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create payment";
      });
    }
  };

  // ==================== ICA Transactions (AR-03) ====================

  getEmptyICAForm(): ICAFormData {
    return {
      fromCompanyId: "",
      toCompanies: [],
      invoiceNumber: "",
      transactionDate: new Date().toISOString().split("T")[0],
      transactionType: "check",
      notes: "",
      // Legacy fields
      toCompanyId: "",
      description: "",
      amount: 0,
    };
  }

  resetICAForm = () => {
    this.icaForm = this.getEmptyICAForm();
  };

  updateICAField = <K extends keyof ICAFormData>(
    field: K,
    value: ICAFormData[K]
  ) => {
    this.icaForm[field] = value;
  };

  /**
   * Add a target company to the ICA form
   */
  addICATargetCompany = (companyId: string) => {
    // Don't add if already exists or is the from company
    if (
      companyId === this.icaForm.fromCompanyId ||
      this.icaForm.toCompanies.some((c) => c.companyId === companyId)
    ) {
      return;
    }
    this.icaForm.toCompanies.push({
      companyId,
      lineItems: [this.createEmptyICALineItem()],
    });
  };

  /**
   * Remove a target company from the ICA form
   */
  removeICATargetCompany = (companyId: string) => {
    this.icaForm.toCompanies = this.icaForm.toCompanies.filter(
      (c) => c.companyId !== companyId
    );
  };

  /**
   * Create an empty line item
   */
  createEmptyICALineItem = (): ICALineItem => ({
    id: `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    amount: 0,
    glAccountId: "gl-888",
    glAccountName: "888 - ICA Clearing",
    description: "",
  });

  /**
   * Add a line item to a target company
   */
  addICALineItem = (companyId: string) => {
    const company = this.icaForm.toCompanies.find((c) => c.companyId === companyId);
    if (company) {
      company.lineItems.push(this.createEmptyICALineItem());
    }
  };

  /**
   * Remove a line item from a target company
   */
  removeICALineItem = (companyId: string, lineItemId: string) => {
    const company = this.icaForm.toCompanies.find((c) => c.companyId === companyId);
    if (company && company.lineItems.length > 1) {
      company.lineItems = company.lineItems.filter((li) => li.id !== lineItemId);
    }
  };

  /**
   * Update a line item field
   */
  updateICALineItem = (
    companyId: string,
    lineItemId: string,
    field: keyof ICALineItem,
    value: string | number
  ) => {
    const company = this.icaForm.toCompanies.find((c) => c.companyId === companyId);
    if (company) {
      const lineItem = company.lineItems.find((li) => li.id === lineItemId);
      if (lineItem) {
        if (field === "glAccountId") {
          const glAccount = ICA_GL_ACCOUNTS.find((gl) => gl.id === value);
          lineItem.glAccountId = value as string;
          lineItem.glAccountName = glAccount ? `${glAccount.code} - ${glAccount.name}` : "";
        } else {
          (lineItem as any)[field] = value;
        }
      }
    }
  };

  /**
   * Get total amount for a target company
   */
  getICACompanyTotal = (companyId: string): number => {
    const company = this.icaForm.toCompanies.find((c) => c.companyId === companyId);
    if (!company) return 0;
    return company.lineItems.reduce((sum, li) => sum + (li.amount || 0), 0);
  };

  /**
   * Get grand total for all target companies
   */
  get icaFormGrandTotal(): number {
    return this.icaForm.toCompanies.reduce(
      (sum, company) =>
        sum + company.lineItems.reduce((s, li) => s + (li.amount || 0), 0),
      0
    );
  }

  /**
   * Get available companies (not already selected as target)
   */
  get availableICATargetCompanies() {
    const selectedIds = new Set([
      this.icaForm.fromCompanyId,
      ...this.icaForm.toCompanies.map((c) => c.companyId),
    ]);
    return MILLER_COMPANIES.filter((c) => !selectedIds.has(c.id));
  }

  /**
   * Create ICA transaction via ServiceStore - Two-Way Sync (Phase 3.2)
   * Creates AP record on source company AND AR record on destination company
   */
  createICATransaction = async () => {
    this.error = null;

    try {
      const fromCompany = MILLER_COMPANIES.find(
        (c) => c.id === this.icaForm.fromCompanyId
      );

      // Create linked AP and AR records for each target company
      for (let index = 0; index < this.icaForm.toCompanies.length; index++) {
        const targetCompany = this.icaForm.toCompanies[index];
        const toCompany = MILLER_COMPANIES.find(
          (c) => c.id === targetCompany.companyId
        );
        const totalAmount = targetCompany.lineItems.reduce(
          (sum, li) => sum + (li.amount || 0),
          0
        );
        const descriptions = targetCompany.lineItems
          .filter((li) => li.description)
          .map((li) => li.description)
          .join("; ");

        const baseData = {
          fromCompanyId: this.icaForm.fromCompanyId,
          fromCompanyName: fromCompany?.name || "",
          toCompanyId: targetCompany.companyId,
          toCompanyName: toCompany?.name || "",
          invoiceNumber: this.icaForm.invoiceNumber,
          transactionDate: this.icaForm.transactionDate,
          description: descriptions || this.icaForm.notes,
          amount: totalAmount,
          clearingAccountId: "gl-888",
          status: "pending",
          transactionType: this.icaForm.transactionType,
          lineItems: [...targetCompany.lineItems],
        };

        // Create AP record (source company - initiating side)
        const apRecord = await this.icaTransactionsServiceStore?.create({
          ...baseData,
          side: "ap",
          canEditGL: false,
          syncedAt: new Date(),
        });

        // Create AR record (destination company - receiving side)
        const arRecord = await this.icaTransactionsServiceStore?.create({
          ...baseData,
          side: "ar",
          lineItems: targetCompany.lineItems.map((li) => ({ ...li, id: `${li.id}-ar` })),
          canEditGL: true,
          syncedAt: new Date(),
        });

        // Link the two records together
        if (apRecord && arRecord) {
          const apId = (apRecord as any).id;
          const arId = (arRecord as any).id;
          await this.icaTransactionsServiceStore?.patch(apId, { linkedTransactionId: arId });
          await this.icaTransactionsServiceStore?.patch(arId, { linkedTransactionId: apId });
        }
      }

      runInAction(() => {
        this.resetICAForm();
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create ICA transaction";
      });
    }
  };

  /**
   * Get the linked transaction for a given ICA record
   */
  getLinkedICATransaction = (transactionId: string): ICATransaction | undefined => {
    const transaction = this.icaTransactions.find((t) => t.id === transactionId);
    if (!transaction?.linkedTransactionId) return undefined;
    return this.icaTransactions.find((t) => t.id === transaction.linkedTransactionId);
  };

  /**
   * Update GL breakdown for AR side (receiver can edit their GL accounts)
   */
  updateARLineItem = async (transactionId: string, lineItemId: string, field: keyof ICALineItem, value: string | number) => {
    const transaction = this.icaTransactions.find((t) => t.id === transactionId);
    if (!transaction || transaction.side !== "ar" || !transaction.canEditGL) return;

    const updatedLineItems = transaction.lineItems?.map((li) => {
      if (li.id !== lineItemId) return li;
      if (field === "glAccountId") {
        const glAccount = ICA_GL_ACCOUNTS.find((gl) => gl.id === value);
        return { ...li, glAccountId: value as string, glAccountName: glAccount ? `${glAccount.code} - ${glAccount.name}` : "" };
      }
      return { ...li, [field]: value };
    });

    try {
      await this.icaTransactionsServiceStore?.patch(transactionId, { lineItems: updatedLineItems });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to update AR line item";
      });
    }
  };

  /**
   * Sync status between linked ICA records via ServiceStore
   */
  syncICAStatus = async (transactionId: string) => {
    const transaction = this.icaTransactions.find((t) => t.id === transactionId);
    const linked = this.getLinkedICATransaction(transactionId);
    if (transaction && linked) {
      const patchData: Record<string, unknown> = {
        status: transaction.status,
        syncedAt: new Date(),
      };
      if (transaction.status === "acknowledged") {
        patchData.acknowledgedAt = transaction.acknowledgedAt;
        patchData.acknowledgedBy = transaction.acknowledgedBy;
      }
      if (transaction.status === "settled") {
        patchData.settledAt = transaction.settledAt;
        patchData.settledBy = transaction.settledBy;
      }
      try {
        await this.icaTransactionsServiceStore?.patch(linked.id, patchData);
      } catch (err: any) {
        runInAction(() => {
          this.error = err.message || "Failed to sync ICA status";
        });
      }
    }
  };

  /**
   * Acknowledge ICA transaction - syncs to linked record via ServiceStore
   */
  acknowledgeICA = async (transactionId: string) => {
    try {
      const currentUser = (this.rootStore as any).authStore?.currentUser;
      await this.icaTransactionsServiceStore?.patch(transactionId, {
        status: "acknowledged",
        acknowledgedAt: new Date(),
        acknowledgedBy: currentUser?.username || currentUser?.id,
      });
      // Sync to linked record (Phase 3.2)
      await this.syncICAStatus(transactionId);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to acknowledge ICA";
      });
    }
  };

  /**
   * Settle ICA transaction - syncs to linked record via ServiceStore
   */
  settleICA = async (transactionId: string) => {
    try {
      const currentUser = (this.rootStore as any).authStore?.currentUser;
      await this.icaTransactionsServiceStore?.patch(transactionId, {
        status: "settled",
        settledAt: new Date(),
        settledBy: currentUser?.username || currentUser?.id,
      });
      // Sync to linked record (Phase 3.2)
      await this.syncICAStatus(transactionId);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to settle ICA";
      });
    }
  };

  // ==================== Deposit Management ====================

  getEmptyDepositForm(): DepositFormData {
    return {
      depositDate: new Date().toISOString().split("T")[0],
      depositType: "cash_check",
      bankAccountId: "",
      referenceNumber: "",
      selectedPaymentIds: [],
      notes: "",
    };
  }

  resetDepositForm = () => {
    this.depositForm = this.getEmptyDepositForm();
  };

  updateDepositField = <K extends keyof DepositFormData>(
    field: K,
    value: DepositFormData[K]
  ) => {
    this.depositForm[field] = value;

    // Clear selections when deposit type changes
    if (field === "depositType") {
      this.depositForm.selectedPaymentIds = [];
    }
  };

  toggleDepositPaymentSelection = (paymentId: string) => {
    const ids = this.depositForm.selectedPaymentIds;
    const index = ids.indexOf(paymentId);
    if (index === -1) {
      ids.push(paymentId);
    } else {
      ids.splice(index, 1);
    }
  };

  selectAllDepositPayments = () => {
    this.depositForm.selectedPaymentIds = this.undepositedPaymentsByType.map(
      (p) => p.id
    );
  };

  clearDepositPaymentSelection = () => {
    this.depositForm.selectedPaymentIds = [];
  };

  /**
   * Create deposit from selected payments via ServiceStore
   */
  createDeposit = async () => {
    this.error = null;

    try {
      const bankAccount = this.bankAccounts.find(
        (b) => b.id === this.depositForm.bankAccountId
      );

      const depositData = {
        depositDate: this.depositForm.depositDate,
        depositType: this.depositForm.depositType,
        bankAccountId: this.depositForm.bankAccountId,
        bankAccountName: bankAccount?.name || "Unknown Account",
        referenceNumber: this.depositForm.referenceNumber,
        totalAmount: this.depositFormTotal,
        paymentIds: [...this.depositForm.selectedPaymentIds],
        notes: this.depositForm.notes,
      };

      const created = await this.arDepositsServiceStore?.create(depositData);

      // Update payment records with deposit batch ID
      if (created) {
        const depositId = (created as any).id;
        for (const paymentId of this.depositForm.selectedPaymentIds) {
          await this.arPaymentsServiceStore?.patch(paymentId, {
            depositBatchId: depositId,
          });
        }
      }

      runInAction(() => {
        this.resetDepositForm();
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create deposit";
      });
    }
  };

  getDepositTypeLabel = (type: DepositType): string => {
    const labels: Record<DepositType, string> = {
      cash_check: "Cash / Check",
      credit_card: "Credit Card",
      ach: "ACH / Wire",
    };
    return labels[type] || type;
  };

  // ==================== Search (AR-04) ====================

  getEmptySearchCriteria(): ARSearchCriteria {
    return {
      invoiceNumber: "",
      customerName: "",
      phone: "",
      address: "",
      dateFrom: "",
      dateTo: "",
    };
  }

  updateSearchCriteria = <K extends keyof ARSearchCriteria>(
    field: K,
    value: string
  ) => {
    this.searchCriteria[field] = value;
  };

  clearSearch = () => {
    this.searchCriteria = this.getEmptySearchCriteria();
    this.searchResults = [];
  };

  /**
   * Search by invoice, phone, or address (filters local data from ServiceStore)
   */
  search = async () => {
    this.isSearching = true;

    try {
      runInAction(() => {
        let results = [...this.invoices];

        if (this.searchCriteria.invoiceNumber) {
          const search = this.searchCriteria.invoiceNumber.toLowerCase();
          results = results.filter((inv) =>
            inv.invoiceNumber.toLowerCase().includes(search)
          );
        }

        if (this.searchCriteria.customerName) {
          const search = this.searchCriteria.customerName.toLowerCase();
          results = results.filter((inv) =>
            inv.customerName.toLowerCase().includes(search)
          );
        }

        if (this.searchCriteria.phone) {
          const search = this.searchCriteria.phone.replace(/\D/g, "");
          results = results.filter((inv) =>
            this.entities
              .find((e) => e.id === inv.customerId)
              ?.phone?.replace(/\D/g, "")
              .includes(search)
          );
        }

        if (this.searchCriteria.address) {
          const search = this.searchCriteria.address.toLowerCase();
          results = results.filter((inv) =>
            this.entities
              .find((e) => e.id === inv.customerId)
              ?.address?.toLowerCase()
              .includes(search)
          );
        }

        if (this.searchCriteria.dateFrom) {
          results = results.filter(
            (inv) => inv.invoiceDate >= this.searchCriteria.dateFrom
          );
        }

        if (this.searchCriteria.dateTo) {
          results = results.filter(
            (inv) => inv.invoiceDate <= this.searchCriteria.dateTo
          );
        }

        this.searchResults = results;
        this.isSearching = false;
      });
    } catch {
      runInAction(() => {
        this.isSearching = false;
      });
    }
  };

  // ==================== Advanced Search (Popover) ====================

  getEmptyAdvancedSearch(): AdvancedSearchState {
    return {
      isOpen: false,
      quickSearch: "",
      invoice: "",
      customer: "",
      phone: "",
      sourceType: "all",
      exactAmount: "",
      minAmount: "",
      maxAmount: "",
      dateFrom: "",
      dateTo: "",
    };
  }

  openAdvancedSearch = () => {
    this.advancedSearch.isOpen = true;
  };

  closeAdvancedSearch = () => {
    this.advancedSearch.isOpen = false;
  };

  updateAdvancedSearch = <K extends keyof AdvancedSearchState>(
    field: K,
    value: AdvancedSearchState[K]
  ) => {
    this.advancedSearch[field] = value;

    // Clear range when exact amount is set
    if (field === "exactAmount" && value) {
      this.advancedSearch.minAmount = "";
      this.advancedSearch.maxAmount = "";
    }
    // Clear exact when range is set
    if ((field === "minAmount" || field === "maxAmount") && value) {
      this.advancedSearch.exactAmount = "";
    }
  };

  clearAdvancedSearch = () => {
    const wasOpen = this.advancedSearch.isOpen;
    this.advancedSearch = this.getEmptyAdvancedSearch();
    this.advancedSearch.isOpen = wasOpen;
  };

  /**
   * Set date range from aging bucket click
   */
  setDateRangeFromAgingBucket = (bucket: AgingBucket) => {
    const today = new Date();
    let dateFrom = "";
    let dateTo = "";

    switch (bucket) {
      case "current":
        // Due date is today or in the future (not overdue)
        dateFrom = today.toISOString().split("T")[0];
        dateTo = "";
        break;
      case "1-30":
        // 1-30 days overdue
        const days30Ago = new Date(today);
        days30Ago.setDate(days30Ago.getDate() - 30);
        const days1Ago = new Date(today);
        days1Ago.setDate(days1Ago.getDate() - 1);
        dateFrom = days30Ago.toISOString().split("T")[0];
        dateTo = days1Ago.toISOString().split("T")[0];
        break;
      case "31-60":
        // 31-60 days overdue
        const days60Ago = new Date(today);
        days60Ago.setDate(days60Ago.getDate() - 60);
        const days31Ago = new Date(today);
        days31Ago.setDate(days31Ago.getDate() - 31);
        dateFrom = days60Ago.toISOString().split("T")[0];
        dateTo = days31Ago.toISOString().split("T")[0];
        break;
      case "61-90":
        // 61-90 days overdue
        const days90Ago = new Date(today);
        days90Ago.setDate(days90Ago.getDate() - 90);
        const days61Ago = new Date(today);
        days61Ago.setDate(days61Ago.getDate() - 61);
        dateFrom = days90Ago.toISOString().split("T")[0];
        dateTo = days61Ago.toISOString().split("T")[0];
        break;
      case "90+":
        // 90+ days overdue
        const days91Ago = new Date(today);
        days91Ago.setDate(days91Ago.getDate() - 91);
        dateTo = days91Ago.toISOString().split("T")[0];
        dateFrom = "";
        break;
    }

    // Clear other filters and set date range
    this.clearAdvancedSearch();
    this.advancedSearch.dateFrom = dateFrom;
    this.advancedSearch.dateTo = dateTo;
    this.advancedSearch.isOpen = true;
  };

  /**
   * Check if advanced search has any active criteria
   */
  get hasAdvancedSearchCriteria(): boolean {
    return (
      this.advancedSearch.quickSearch.length >= 2 ||
      this.advancedSearch.invoice.length >= 2 ||
      this.advancedSearch.customer.length >= 2 ||
      this.advancedSearch.phone.length >= 2 ||
      this.advancedSearch.exactAmount !== "" ||
      this.advancedSearch.minAmount !== "" ||
      this.advancedSearch.maxAmount !== "" ||
      this.advancedSearch.dateFrom !== "" ||
      this.advancedSearch.dateTo !== "" ||
      this.advancedSearch.sourceType !== "all"
    );
  }

  /**
   * Count active advanced search filters
   */
  get advancedSearchFilterCount(): number {
    return [
      this.advancedSearch.quickSearch.length >= 2,
      this.advancedSearch.invoice.length >= 2,
      this.advancedSearch.customer.length >= 2,
      this.advancedSearch.phone.length >= 2,
      this.advancedSearch.sourceType !== "all",
      this.advancedSearch.exactAmount !== "" ||
        this.advancedSearch.minAmount !== "" ||
        this.advancedSearch.maxAmount !== "",
      this.advancedSearch.dateFrom !== "" || this.advancedSearch.dateTo !== "",
    ].filter(Boolean).length;
  }

  // ==================== Helpers ====================

  getSourceTypeLabel = (type: SourceType): string => {
    const labels: Record<SourceType, string> = {
      customer: "Customer",
      vendor: "Vendor",
      employee: "Employee",
      intercompany: "Intercompany",
      other: "Other",
    };
    return labels[type] || type;
  };

  getPaymentTypeLabel = (type: PaymentType): string => {
    const labels: Record<PaymentType, string> = {
      invoice_payment: "Invoice Payment",
      refund: "Refund",
      ica_settlement: "ICA Settlement",
      misc_income: "Misc Income",
    };
    return labels[type] || type;
  };

  getPaymentMethodLabel = (method: PaymentMethod): string => {
    const labels: Record<PaymentMethod, string> = {
      check: "Check",
      ach: "ACH",
      wire: "Wire Transfer",
      credit_card: "Credit Card",
      cash: "Cash",
    };
    return labels[method] || method;
  };

  getICAStatusColor = (
    status: ICAStatus
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<ICAStatus, any> = {
      pending: "warning",
      acknowledged: "info",
      settled: "success",
    };
    return colors[status] || "default";
  };

  // ==================== Data Loading ====================

  loadData = async () => {
    this.error = null;

    try {
      await Promise.all([
        this.paymentEntitiesServiceStore?.find({ query: { $limit: 200 } }),
        this.arInvoicesServiceStore?.find({ query: { $limit: 200 } }),
        this.arPaymentsServiceStore?.find({ query: { $limit: 200 } }),
        this.arDepositsServiceStore?.find({ query: { $limit: 200 } }),
        this.icaTransactionsServiceStore?.find({ query: { $limit: 200 } }),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to load AR data";
      });
    }
  };

  /**
   * Open print dialog for an ICA transaction (Phase 3.3)
   */
  printICAInvoice = (transactionId: string) => {
    const transaction = this.icaTransactions.find((t) => t.id === transactionId);
    if (transaction) {
      this.selectedICA = transaction;
      this.rootStore.routerStore.openDialog({
        dialogName: "icaPrint",
        dialogContext: transactionId,
      });
    }
  };

  /**
   * View ICA transaction details
   */
  viewICADetails = (transactionId: string) => {
    const transaction = this.icaTransactions.find((t) => t.id === transactionId);
    if (transaction) {
      this.selectedICA = transaction;
    }
  };

  reset = () => {
    this.clearFilters();
    this.resetQuickPaymentForm();
    this.resetICAForm();
    this.resetDepositForm();
    this.clearSearch();
    this.selectedPayment = null;
    this.selectedICA = null;
    this.error = null;
  };
}

export default AccountsReceivableStore;
